import { useState, useEffect, useMemo, useRef } from 'react';
import { getCategoryV1, getActionsV1 } from '../../../utils/requests';
import { detectActionsLanguage, getCategoryNameByLocale } from '../utils';

interface CategoryItem {
  category_ID: string;
  category_Name_TH: string;
  category_Name_EN: string;
  category_Name_CN: string;
  category_Desc: string | null;
  category_Status: string;
  category_Sort: string;
  type: string;
}

interface ActionItem {
  id: string;
  action: string;
}

interface ApiDataProps {
  categories?: CategoryItem[];
  actions?: ActionItem[];
}

export const useApiData = (locale: string, staticData?: ApiDataProps) => {
  const [categoryData, setCategoryData] = useState<CategoryItem[]>([]);
  const [actionsData, setActionsData] = useState<ActionItem[]>([]);
  const [lastFetchedLocale, setLastFetchedLocale] = useState<string>('');

  // ตรวจสอบว่ามีข้อมูล static data ครบแล้วหรือไม่
  const hasStaticCategories = staticData?.categories && staticData.categories.length > 0;
  const hasStaticActions = staticData?.actions && staticData.actions.length > 0;
  
  // ตรวจสอบภาษาของ static actions
  const staticActionsLanguage = hasStaticActions ? detectActionsLanguage(staticData.actions!) : null;
  
  // ตัดสินใจว่าต้อง fetch ข้อมูลใหม่หรือไม่
  const needsFreshActions = !hasStaticActions || 
    (staticActionsLanguage && staticActionsLanguage !== locale) ||
    lastFetchedLocale !== locale;
  
  const needsFreshCategories = !hasStaticCategories;

  useEffect(() => {
    // ถ้าไม่ต้อง fetch ข้อมูลใหม่และมี static data ให้ใช้ static data
    if (!needsFreshActions && !needsFreshCategories && hasStaticCategories && hasStaticActions) {
      setCategoryData(staticData.categories!);
      setActionsData(staticData.actions!);
      setLastFetchedLocale(locale);
      return;
    }

    // fetch ข้อมูลใหม่
    const fetchMissingData = async () => {
      try {
        // เรียก Category API เฉพาะเมื่อไม่มี static data
        if (needsFreshCategories) {
          console.log('Fetching fresh category data due to missing static data');
          const categoryResponse = await getCategoryV1();
          if (categoryResponse && categoryResponse.data) {
            setCategoryData(categoryResponse.data);
          }
        } else {
          setCategoryData(staticData.categories!);
        }

        // เรียก Actions API เฉพาะเมื่อจำเป็น
        if (needsFreshActions) {
          if (staticActionsLanguage && staticActionsLanguage !== locale) {
            console.log(`Fetching fresh actions data due to language mismatch: static=${staticActionsLanguage}, current=${locale}`);
          } else if (!hasStaticActions) {
            console.log('Fetching fresh actions data due to missing static data');
          } else {
            console.log(`Fetching fresh actions data due to locale change: ${lastFetchedLocale} -> ${locale}`);
          }
          
          const actionsResponse = await getActionsV1(locale);
          if (actionsResponse && actionsResponse.data) {
            setActionsData(actionsResponse.data);
            setLastFetchedLocale(locale);
          }
        } else {
          setActionsData(staticData.actions!);
          setLastFetchedLocale(locale);
        }
      } catch (error) {
        console.error('Error fetching API data:', error);
      }
    };

    fetchMissingData();
  }, [locale, needsFreshActions, needsFreshCategories, hasStaticCategories, hasStaticActions, staticActionsLanguage, lastFetchedLocale, staticData?.categories, staticData?.actions]);

  const filteredActions = useMemo(() => {
    if (actionsData && actionsData.length > 0) {
      return actionsData;
    }
    
    return [];
  }, [actionsData]);

  const actionMapping = useMemo(() => {
    const mapping: Record<string, string> = {};
    
    if (actionsData && actionsData.length > 0) {
      actionsData.forEach(action => {
        const actionId = String(action.id);
        const actionName = action.action;
        mapping[actionId] = actionName;
      });
    }
    
    return mapping;
  }, [actionsData]);

  const filteredCategories = useMemo(() => {
    if (categoryData && categoryData.length > 0) {
      return categoryData;
    }
    
    return [];
  }, [categoryData]);

  const categoryMapping = useMemo(() => {
    const mapping: Record<string, string> = {};
    
    if (categoryData && categoryData.length > 0) {
      categoryData.forEach(category => {
        const categoryId = String(category.category_ID);
        const categoryName = getCategoryNameByLocale(category, locale);
        mapping[categoryId] = categoryName;
      });
    }
    
    return mapping;
  }, [categoryData, locale]);

  return {
    categoryData,
    actionsData,
    filteredActions,
    actionMapping,
    filteredCategories,
    categoryMapping
  };
}; 